import React, { useState, useEffect } from 'react';
import '@assets/css/common/style_main.css';
import '@assets/css/common/style_table.css';
import '@assets/css/common/style_modal.css';
import '@assets/css/common/style_modal_role.css';
import PaginationZeroBased from '@components/common/PaginationZeroBased.jsx';
import accountApi from '@services/accountApi.jsx';
import branchApi from '@services/branchApi.jsx'; // 지점 API
import roleApi from '@services/roleApi.jsx'; // 역할 API

const AccountList = () => {
    // 상태 관리
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [branches, setBranches] = useState([]);
    const [roles, setRoles] = useState([]);

    // 페이징 관련 상태
    const [currentPage, setCurrentPage] = useState(0); // 백엔드는 0부터 시작하는 페이지 인덱스 사용
    const [totalPages, setTotalPages] = useState(1);
    const [pageSize] = useState(10);

    // 정렬 관련 상태
    const [sortOrder, setSortOrder] = useState('latest'); // 'latest' 또는 'name'

    // 체크박스 상태 관리
    const [selectAll, setSelectAll] = useState(false);
    const [selectedItems, setSelectedItems] = useState([]);

    // 모달 상태 관리
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [showAlertModal, setShowAlertModal] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [confirmMessage, setConfirmMessage] = useState('');
    const [confirmCallback, setConfirmCallback] = useState(null);
    const [currentAccount, setCurrentAccount] = useState(null);

    // 모달 입력값 상태 관리
    const [editModalInputs, setEditModalInputs] = useState({
        id: '',
        email: '',
        username: '',
        phone: '',
        isApproved: false,
        isActive: false,
        userRoles: []
    });

    // 사용자 목록 조회 함수
    const fetchAccounts = async () => {
        setLoading(true);
        try {
            // API에서 사용할 정렬 방식 변환
            const sortParam = sortOrder === 'latest' ? 'createdAt,desc' : 'username,asc';

            // API 호출
            const response = await accountApi.getAccounts(currentPage, pageSize, sortParam);

            // 응답 데이터 처리
            setAccounts(response.content || []);
            setTotalPages(response.totalPages || 1);
            setError(null);

            // 선택된 항목 초기화
            setSelectedItems([]);
            setSelectAll(false);
        } catch (err) {
            setError('사용자 목록을 불러오는 데 실패했습니다.');
            console.error('Error fetching accounts:', err);
        } finally {
            setLoading(false);
        }
    };
    
    // 초기 데이터 불러오기 (브랜치, 역할 등)
    const fetchInitialData = async () => {
        try {
            // 지점 목록 가져오기
            const branchesResponse = await branchApi.getBranches();

            // API 응답 구조 확인 및 데이터 추출
            const branchesData = branchesResponse?.data?.content ||
                               branchesResponse?.content ||
                               branchesResponse?.data ||
                               branchesResponse || [];

            if (Array.isArray(branchesData)) {
                setBranches(branchesData);
            } else {
                console.warn('지점 데이터가 배열이 아닙니다:', branchesResponse);
                setBranches([]);
            }
            
            // 역할 목록 가져오기
            const rolesResponse = await roleApi.getRoles();
            
            if (rolesResponse) {
                // API 응답이 이미 data 속성을 포함하고 있을 수 있음
                const roleData = rolesResponse.content || rolesResponse.data?.content || rolesResponse || [];
                
                // 배열인지 확인
                if (Array.isArray(roleData)) {
                    // requiresBranch 필드가 없는 경우 roleType에 따라 설정
                    const rolesWithBranchRequirement = roleData.map(role => ({
                        ...role,
                        requiresBranch: role.requiresBranch !== undefined 
                            ? role.requiresBranch 
                            : role.roleType !== 'ROLE_ADMIN' // ROLE_ADMIN이 아닌 경우 모두 지점 필요
                    }));
                    
                    // roleType 기준으로 정렬
                    const sortedRoles = rolesWithBranchRequirement.sort((a, b) => {
                        const roleTypeOrder = {
                            "ROLE_ADMIN": 1,
                            "ROLE_MANAGER": 2,
                            "ROLE_USER": 3
                        };
                        return (roleTypeOrder[a.roleType] || 99) - (roleTypeOrder[b.roleType] || 99);
                    });
                    
                    setRoles(sortedRoles);
                } else {
                    console.warn('역할 데이터가 배열이 아닙니다:', roleData);
                    setRoles([]);
                }
            } else {
                console.warn('역할 데이터를 불러올 수 없습니다.');
                // 기본 역할 설정
                setRoles([
                    { id: 1, name: '최고 관리자', roleType: 'ROLE_ADMIN', requiresBranch: false },
                    { id: 2, name: '관리자', roleType: 'ROLE_MANAGER', requiresBranch: true },
                    { id: 3, name: '일반 사용자', roleType: 'ROLE_USER', requiresBranch: true }
                ]);
            }
        } catch (error) {
            console.error('초기 데이터 로드 실패:', error);
            setError('초기 데이터를 불러오는 데 실패했습니다.');
        }
    };
    
    // 페이지나 정렬 방식이 변경될 때마다 데이터 다시 로드
    useEffect(() => {
        fetchAccounts();
    }, [currentPage, sortOrder]);
    
    // 초기 로드
    useEffect(() => {
        fetchInitialData();
    }, []);
    
    // 정렬 변경 처리 함수
    const handleSortChange = (e) => {
        setSortOrder(e.target.value);
        setCurrentPage(0); // 정렬 변경 시 첫 페이지로 이동
    };

    // 페이지 이동 함수
    const goToPage = (page) => {
        if (page >= 0 && page < totalPages) {
            setCurrentPage(page);
        }
    };

    // 체크박스 전체 선택/해제 핸들러
    const handleSelectAll = (e) => {
        setSelectAll(e.target.checked);
        // 모든 계정 ID 또는 빈 배열로 설정
        setSelectedItems(e.target.checked ? accounts.map(account => account.id) : []);
    };

    // 개별 체크박스 핸들러
    const handleItemSelect = (id, e) => {
        if (e.target.checked) {
            setSelectedItems([...selectedItems, id]);
        } else {
            setSelectedItems(selectedItems.filter(item => item !== id));
        }

        // 전체 선택 상태 업데이트
        if (e.target.checked && selectedItems.length + 1 === accounts.length) {
            setSelectAll(true);
        } else if (!e.target.checked && selectAll) {
            setSelectAll(false);
        }
    };

    // 편집 모달 열기 핸들러
    const handleEditClick = (account) => {
        setCurrentAccount(account);
        
        // userRoles 데이터 변환: account.roles 형식을 userRoles 형식으로 변환
        const userRoles = account.userRoles || (account.roles && account.roles.length > 0 
            ? account.roles.map(role => ({
                roleId: role.id || role.roleId,
                branchId: role.branchId || null
            }))
            : [{
                roleId: null,
                branchId: null
            }]
        );
        
        setEditModalInputs({
            id: account.id || '',
            email: account.email || '',
            username: account.username || '',
            phone: account.phone || '',
            isApproved: account.isApproved,
            isActive: account.isActive,
            userRoles: userRoles
        });
        
        setShowEditModal(true);
    };

    // 편집 모달 입력값 변경 핸들러
    const handleEditInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setEditModalInputs(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    // 역할 선택 시 해당 역할이 브랜치를 필요로 하는지 확인
    const checkRoleRequiresBranch = (roleId) => {
        if (!roleId) return true; // 기본값: 브랜치 필요
        const selectedRole = roles.find(role => role.id === Number(roleId));
        
        // requiresBranch가 없으면 roleType으로 판단
        if (selectedRole) {
            if (selectedRole.requiresBranch !== undefined) {
                return selectedRole.requiresBranch;
            }
            // ROLE_ADMIN 타입이면 지점 불필요
            return selectedRole.roleType !== 'ROLE_ADMIN';
        }
        
        return true; // 기본값: 브랜치 필요
    };

    // 역할-지점 추가 함수
    const addUserRole = () => {
        const defaultRoleId = roles.length > 0 ? roles[0].id : 1;
        
        setEditModalInputs(prevData => ({
            ...prevData,
            userRoles: [
                ...prevData.userRoles,
                { roleId: defaultRoleId, branchId: null }
            ]
        }));
    };

    // 역할-지점 제거 함수
    const removeUserRole = (index) => {
        setEditModalInputs(prevData => {
            const newUserRoles = [...prevData.userRoles];
            newUserRoles.splice(index, 1);
            return {
                ...prevData,
                userRoles: newUserRoles
            };
        });
    };

    // 역할-지점 수정 함수
    const updateUserRole = (index, field, value) => {
        setEditModalInputs(prevData => {
            const newUserRoles = [...prevData.userRoles];

            // roleId가 변경된 경우
            if (field === 'roleId') {
                const selectedRole = roles.find(r => r.id === value);

                if (!checkRoleRequiresBranch(value)) {
                    // 브랜치가 필요하지 않은 역할 (ROLE_ADMIN)
                    newUserRoles[index] = {
                        ...newUserRoles[index],
                        [field]: value,
                        branchId: null // 브랜치 ID를 null로 설정
                    };
                } else if (selectedRole && selectedRole.branchId) {
                    // 브랜치가 이미 설정된 역할 (특정 지점의 ROLE_MANAGER 등)
                    newUserRoles[index] = {
                        ...newUserRoles[index],
                        [field]: value,
                        branchId: selectedRole.branchId // 역할에 설정된 브랜치 ID 자동 설정
                    };
                } else {
                    // 브랜치가 필요하지만 설정되지 않은 역할
                    newUserRoles[index] = {
                        ...newUserRoles[index],
                        [field]: value
                    };
                }
            } else {
                newUserRoles[index] = {
                    ...newUserRoles[index],
                    [field]: value
                };
            }

            return {
                ...prevData,
                userRoles: newUserRoles
            };
        });
    };

    // 편집 모달 확인 핸들러
    const handleEditConfirm = async () => {
        // 입력값 검증
        if (!editModalInputs.username) {
            setAlertMessage('이름을 확인해주세요.');
            setShowAlertModal(true);
            return;
        }

        if (!editModalInputs.phone) {
            setAlertMessage('전화번호를 확인해주세요.');
            setShowAlertModal(true);
            return;
        }

        // userRoles 검증 - 빈 배열이 아닌 경우에만 검증
        if (editModalInputs.userRoles.length > 0) {
            const invalidRoles = editModalInputs.userRoles.filter(userRole => {
                if (!userRole.roleId) return true;
                if (checkRoleRequiresBranch(userRole.roleId) && !userRole.branchId) return true;
                return false;
            });

            if (invalidRoles.length > 0) {
                setAlertMessage('모든 권한의 역할과 지점을 선택해주세요.');
                setShowAlertModal(true);
                return;
            }
        }

        try {
            // API 호출로 계정 수정
            await accountApi.updateAccount(currentAccount.id, {
                id: editModalInputs.id,
                email: editModalInputs.email,
                username: editModalInputs.username,
                phone: editModalInputs.phone,
                isApproved: editModalInputs.isApproved,
                isActive: editModalInputs.isActive,
                userRoles: editModalInputs.userRoles // userRoles 정보 추가
            });

            // 성공 알림
            setAlertMessage('계정이 수정되었습니다.');
            setShowAlertModal(true);

            // 모달 닫기
            setShowEditModal(false);

            // 데이터 새로고침
            fetchAccounts();
        } catch (error) {
            setAlertMessage('계정 수정에 실패했습니다.');
            setShowAlertModal(true);
        }
    };

    // 계정 상세정보 보기 처리 함수
    const handleInfoClick = (account) => {
        setCurrentAccount(account);
        setShowDetailModal(true);
    };

    // 계정 삭제 버튼 핸들러
    const handleDeleteClick = (account) => {
        setCurrentAccount(account);
        setConfirmMessage(`${account.accountName || account.email} 계정을 삭제하시겠습니까?`);
        setConfirmCallback(() => async () => {
            try {
                // API를 통한 계정 삭제
                await accountApi.deleteAccount(account.id, {
                    id: account.id,
                    email: account.email,
                    isDeleted: true,
                });

                // 성공 알림
                setAlertMessage('계정이 삭제되었습니다.');
                setShowAlertModal(true);

                // 데이터 새로고침
                fetchAccounts();
            } catch (error) {
                setAlertMessage('계정 삭제에 실패했습니다.');
                setShowAlertModal(true);
            }
        });
        setShowConfirmModal(true);
    };

    // 확인 모달 확인 버튼 핸들러
    const handleConfirmModalConfirm = () => {
        if (confirmCallback) {
            confirmCallback();
        }
        setShowConfirmModal(false);
    };

    // 모달 닫기 함수
    const handleCloseModal = () => {
        setShowEditModal(false);
        setShowDetailModal(false);
        setShowAlertModal(false);
        setShowConfirmModal(false);
    };

    // 알림 모달만 닫는 함수
    const handleCloseAlertModal = () => {
        setShowAlertModal(false);
    };

    // 확인 모달만 닫는 함수
    const handleCloseConfirmModal = () => {
        setShowConfirmModal(false);
    };

    // 역할 정보를 포맷팅하는 함수
    const formatRoles = (account) => {
        // userRoles 형식 우선 확인
        if (account.userRoles && Array.isArray(account.userRoles) && account.userRoles.length > 0) {
            return account.userRoles.map(userRole => {
                const role = roles.find(r => r.id === userRole.roleId);
                const branch = branches.find(b => b.id === userRole.branchId);
                
                if (!role) return '';
                
                return branch 
                    ? `${role.name}(${branch.name})`
                    : role.name;
            }).filter(r => r).join(", ");
        }
        
        // 기존 roles 형식 (하위 호환성)
        if (account.roles && Array.isArray(account.roles) && account.roles.length > 0) {
            return account.roles.map(role => {
                return role.branchName
                    ? `${role.name}(${role.branchName})`
                    : role.name;
            }).join(", ");
        }
        
        // roleName 속성 확인 (단일 역할)
        if (account.roleName) {
            return account.roleName;
        }
        
        return "-";
    };


    // 일괄 삭제 핸들러
    const handleBulkDelete = () => {
        if (selectedItems.length === 0) return;
        
        setConfirmMessage(`선택한 ${selectedItems.length}개의 계정을 삭제하시겠습니까?`);
        setConfirmCallback(() => async () => {
            try {
                // 선택된 모든 계정 삭제
                for (const accountId of selectedItems) {
                    await accountApi.deleteAccount(accountId, {
                        id: accountId,
                        isDeleted: true,
                    });
                }
                
                // 성공 알림
                setAlertMessage('선택한 계정이 삭제되었습니다.');
                setShowAlertModal(true);
                
                // 선택 초기화
                setSelectedItems([]);
                setSelectAll(false);
                
                // 데이터 새로고침
                fetchAccounts();
            } catch (error) {
                setAlertMessage('계정 삭제에 실패했습니다.');
                setShowAlertModal(true);
            }
        });
        setShowConfirmModal(true);
    };

    return (
        <>
            <div className="page-title-container">
                <h1 className="page-title">계정</h1>
                <div className="page-title-icon">
                    <a href="#" id="add-account-btn" title="계정 추가"></a>
                </div>
            </div>

            <div className="page-container">
                {/* 필터 드롭다운 */}
                <div className="filter-dropdown-container-row">
                    {/* 일괄 삭제 버튼 */}
                    <div className={`bulk-delete-container ${selectedItems.length > 2 ? 'active' : ''}`}>
                        <button className="bulk-delete-btn" onClick={handleBulkDelete}>
                            일괄 삭제 ({selectedItems.length})
                        </button>
                    </div>

                    <div className="filter-dropdown">
                        <select
                            id="account-filter"
                            value={sortOrder}
                            onChange={handleSortChange}
                        >
                            <option value="latest">최신순</option>
                            <option value="name">이름순</option>
                        </select>
                    </div>
                </div>

                {/* 계정 테이블 */}
                <div className="common-table-container">
                    {loading ? (
                        <div className="loading-indicator">로딩 중...</div>
                    ) : error ? (
                        <div className="error-message">{error}</div>
                    ) : (
                        <table className="common-table">
                            <thead>
                                <tr>
                                    <th>
                                        <div className="checkbox-container">
                                            <input
                                                type="checkbox"
                                                id="select-all"
                                                checked={selectAll}
                                                onChange={handleSelectAll}
                                            />
                                            <label
                                                htmlFor="select-all"
                                                className="custom-checkbox"
                                            ></label>
                                        </div>
                                    </th>
                                    <th>NO</th>
                                    <th>신청 계정</th>
                                    <th>신청 날짜</th>
                                    <th>승인</th>
                                    <th>활성여부</th>
                                    <th>상세정보</th>
                                    <th>수정</th>
                                    <th>삭제</th>
                                </tr>
                            </thead>
                            <tbody>
                                {accounts.length > 0 ? (
                                    accounts.map((account, index) => (
                                        <tr key={account.id}>
                                            <td>
                                                <div className="checkbox-container">
                                                    <input
                                                        type="checkbox"
                                                        id={`account-${account.id}`}
                                                        checked={selectedItems.includes(
                                                            account.id
                                                        )}
                                                        onChange={(e) =>
                                                            handleItemSelect(
                                                                account.id,
                                                                e
                                                            )
                                                        }
                                                    />
                                                    <label
                                                        htmlFor={`account-${account.id}`}
                                                        className="custom-checkbox"
                                                    ></label>
                                                </div>
                                            </td>
                                            <td>
                                                {currentPage * pageSize +
                                                    index +
                                                    1}
                                            </td>
                                            <td>
                                                {account.accountName ||
                                                    account.email}
                                            </td>
                                            <td>
                                                {account.regDate ||
                                                    (account.createdAt
                                                        ? new Date(
                                                              account.createdAt
                                                          )
                                                              .toLocaleDateString(
                                                                  "ko-KR",
                                                                  {
                                                                      year: "numeric",
                                                                      month: "2-digit",
                                                                      day: "2-digit",
                                                                  }
                                                              )
                                                              .replace(
                                                                  /\. /g,
                                                                  ""
                                                              )
                                                              .replace(
                                                                  /\.$/,
                                                                  ""
                                                              )
                                                        : "-")}
                                            </td>
                                            <td>
                                                {account.isApproved ? "Y" : "N"}
                                            </td>
                                            <td>
                                                {account.isActive ? "Y" : "N"}
                                            </td>
                                            <td>
                                                <button
                                                    className="info-btn"
                                                    onClick={() =>
                                                        handleInfoClick(account)
                                                    }
                                                >
                                                    <i className="ri-information-line"></i>
                                                </button>
                                            </td>
                                            <td>
                                                <button
                                                    className="edit-btn"
                                                    onClick={() =>
                                                        handleEditClick(account)
                                                    }
                                                >
                                                    <i className="ri-pencil-line"></i>
                                                </button>
                                            </td>
                                            <td>
                                                <button
                                                    className="delete-btn"
                                                    onClick={() =>
                                                        handleDeleteClick(
                                                            account
                                                        )
                                                    }
                                                >
                                                    <i className="ri-close-line"></i>
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="9" className="no-data">
                                            데이터가 없습니다.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* 페이지네이션 */}
                {!loading &&
                    !error &&
                    accounts.length > 0 && (
                        <PaginationZeroBased
                            page={currentPage}
                            totalPages={totalPages}
                            totalElements={accounts.length}
                            size={pageSize}
                            hasNext={currentPage < totalPages - 1}
                            hasPrevious={currentPage > 0}
                            onPageChange={goToPage}
                        />
                    )}
            </div>

            {/* 계정 편집 모달 */}
            {showEditModal && currentAccount && (
                <div className="modal-overlay active" id="edit-account-modal">
                    <div className="modal-container">
                        <div className="modal-header">
                            <h3 className="modal-title">계정 수정</h3>
                            <button
                                className="modal-close"
                                onClick={handleCloseModal}
                            >
                                &times;
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="modal-form-group">
                                <label className="modal-label">이메일</label>
                                <input
                                    type="text"
                                    className="modal-input"
                                    id="account-email"
                                    name="email"
                                    value={editModalInputs.email}
                                    readOnly
                                />
                            </div>

                            <div className="modal-form-group">
                                <label className="modal-label">이름</label>
                                <div className="modal-input-with-button">
                                    <input
                                        type="text"
                                        className="modal-input"
                                        id="account-username"
                                        name="username"
                                        value={editModalInputs.username || ""}
                                        onChange={handleEditInputChange}
                                        placeholder="이름을 입력하세요"
                                    />
                                </div>
                            </div>

                            <div className="modal-form-group">
                                <label className="modal-label">전화번호</label>
                                <div className="modal-input-with-button">
                                    <input
                                        type="text"
                                        className="modal-input"
                                        id="account-phone"
                                        name="phone"
                                        value={editModalInputs.phone || ""}
                                        onChange={handleEditInputChange}
                                        placeholder="전화번호를 입력하세요"
                                    />
                                </div>
                            </div>

                            {/* 역할-지점 관리 섹션 */}
                            <div className="modal-form-section">
                                <h4 className="modal-section-title">권한 설정</h4>
                                <p className="modal-section-description">사용자의 역할과 접근 가능한 지점을 설정하세요.</p>

                                {editModalInputs.userRoles.map((userRole, index) => (
                                    <div key={index} className="user-role-item">
                                        <div className="role-header">
                                            <h5>권한 #{index + 1}</h5>
                                            {editModalInputs.userRoles.length > 1 && (
                                                <button
                                                    type="button"
                                                    className="remove-button"
                                                    onClick={() => removeUserRole(index)}
                                                >
                                                    삭제
                                                </button>
                                            )}
                                        </div>

                                        <div className="role-fields">
                                            <div className="modal-form-group">
                                                <label className="modal-label">역할</label>
                                                <div className="modal-select-container">
                                                    <select
                                                        className="modal-select"
                                                        value={userRole.roleId || ''}
                                                        onChange={(e) => updateUserRole(index, 'roleId', Number(e.target.value))}
                                                        required
                                                    >
                                                        <option value="">역할을 선택하세요</option>
                                                        {Array.isArray(roles) && roles.map(role => (
                                                            <option key={role.id} value={role.id}>
                                                                {role.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    <i className="ri-arrow-down-s-line modal-select-arrow"></i>
                                                </div>
                                            </div>

                                            {userRole.roleId && checkRoleRequiresBranch(userRole.roleId) && (
                                                <div className="modal-form-group">
                                                    <label className="modal-label">지점</label>
                                                    <div className="modal-select-container">
                                                        <select
                                                            className="modal-select"
                                                            value={userRole.branchId || ''}
                                                            onChange={(e) => updateUserRole(
                                                                index,
                                                                'branchId',
                                                                e.target.value ? Number(e.target.value) : null
                                                            )}
                                                            disabled={(() => {
                                                                // 선택된 역할이 이미 특정 지점과 연결되어 있는지 확인
                                                                const selectedRole = roles.find(r => r.id === userRole.roleId);
                                                                return selectedRole && selectedRole.branchId;
                                                            })()}
                                                            required
                                                        >
                                                            <option value="">지점을 선택하세요</option>
                                                            {Array.isArray(branches) && branches.map(branch => (
                                                                <option key={branch.id} value={branch.id}>
                                                                    {branch.name}
                                                                </option>
                                                            ))}
                                                        </select>
                                                        <i className="ri-arrow-down-s-line modal-select-arrow"></i>
                                                    </div>
                                                    {(() => {
                                                        const selectedRole = roles.find(r => r.id === userRole.roleId);
                                                        if (selectedRole && selectedRole.branchId) {
                                                            return (
                                                                <small style={{ color: '#666', display: 'block', marginTop: '5px' }}>
                                                                    * 이 역할은 {branches.find(b => b.id === selectedRole.branchId)?.name || '특정 지점'}에 자동 할당됩니다.
                                                                </small>
                                                            );
                                                        }
                                                        return null;
                                                    })()}
                                                </div>
                                            )}
                                            
                                            {userRole.roleId && !checkRoleRequiresBranch(userRole.roleId) && (
                                                <div className="modal-form-group">
                                                    <p className="info-text">이 역할은 지점 선택이 필요하지 않습니다. 모든 지점에 접근 가능합니다.</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}

                                <button
                                    type="button"
                                    className="add-role-button"
                                    onClick={addUserRole}
                                >
                                    + 권한 추가
                                </button>
                            </div>

                            <div className="modal-form-group toggle-group">
                                <div className="toggle-label">승인</div>
                                <div className="toggle-switch-container">
                                    <label className="toggle-switch">
                                        <input
                                            type="checkbox"
                                            id="account-approved"
                                            name="isApproved"
                                            checked={editModalInputs.isApproved}
                                            onChange={handleEditInputChange}
                                        />
                                        <span className="toggle-slider"></span>
                                    </label>
                                </div>
                            </div>

                            <div className="modal-form-group toggle-group">
                                <div className="toggle-label">활성여부</div>
                                <div className="toggle-switch-container">
                                    <label className="toggle-switch">
                                        <input
                                            type="checkbox"
                                            id="account-active"
                                            name="isActive"
                                            checked={editModalInputs.isActive}
                                            onChange={handleEditInputChange}
                                        />
                                        <span className="toggle-slider"></span>
                                    </label>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button
                                className="modal-btn modal-btn-cancel"
                                onClick={handleCloseModal}
                            >
                                취소
                            </button>
                            <button
                                className="modal-btn modal-btn-primary"
                                onClick={handleEditConfirm}
                            >
                                저장
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 계정 상세정보 모달 */}
            {showDetailModal && currentAccount && (
                <div className="modal-overlay active" id="account-detail-modal">
                    <div className="modal-container">
                        <div className="modal-header">
                            <h3 className="modal-title">계정 상세정보</h3>
                            <button
                                className="modal-close"
                                onClick={handleCloseModal}
                            >
                                &times;
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="detail-item-container">
                                <span className="detail-label">이메일</span>
                                <span className="detail-content">
                                    {currentAccount.email}
                                </span>
                            </div>
                            <div className="detail-item-container">
                                <span className="detail-label">이름</span>
                                <span className="detail-content">
                                    {currentAccount.username || ""}
                                </span>
                            </div>
                            <div className="detail-item-container">
                                <span className="detail-label">전화번호</span>
                                <span className="detail-content">
                                    {currentAccount.phone || ""}
                                </span>
                            </div>
                            <div className="detail-item-container">
                                <span className="detail-label">권한</span>
                                <span className="detail-content">
                                    {formatRoles(currentAccount)}
                                </span>
                            </div>
                            <div className="detail-item-container">
                                <span className="detail-label">승인 상태</span>
                                <span className="detail-content">
                                    {currentAccount.isApproved
                                        ? "승인됨"
                                        : "미승인"}
                                </span>
                            </div>
                            <div className="detail-item-container">
                                <span className="detail-label">
                                    활성화 상태
                                </span>
                                <span className="detail-content">
                                    {currentAccount.isActive
                                        ? "활성화됨"
                                        : "비활성화됨"}
                                </span>
                            </div>
                            <div className="detail-item-container">
                                <span className="detail-label">가입일</span>
                                <span className="detail-content">
                                    {currentAccount.regDate
                                        ? currentAccount.regDate.replace(
                                              /(\d{4})(\d{2})(\d{2})/,
                                              "$1-$2-$3"
                                          )
                                        : currentAccount.createdAt
                                          ? new Date(currentAccount.createdAt)
                                                .toLocaleDateString("ko-KR")
                                                .replace(/\. /g, "-")
                                                .replace(/\.$/, "")
                                          : "-"}
                                </span>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button
                                className="modal-btn modal-btn-primary"
                                onClick={handleCloseModal}
                            >
                                확인
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 알림 모달 */}
            {showAlertModal && (
                <div className="modal-overlay active" id="alert-modal">
                    <div className="modal-container">
                        <div className="modal-header">
                            <h3 className="modal-title">알림</h3>
                            <button
                                className="modal-close"
                                onClick={handleCloseAlertModal}
                            >
                                &times;
                            </button>
                        </div>
                        <div className="modal-body">
                            <p>{alertMessage}</p>
                        </div>
                        <div className="modal-footer">
                            <button
                                className="modal-btn modal-btn-primary"
                                onClick={handleCloseAlertModal}
                            >
                                확인
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 확인 모달 */}
            {showConfirmModal && (
                <div className="modal-overlay active" id="confirm-modal">
                    <div className="modal-container">
                        <div className="modal-header">
                            <h3 className="modal-title">확인</h3>
                            <button
                                className="modal-close"
                                onClick={handleCloseConfirmModal}
                            >
                                &times;
                            </button>
                        </div>
                        <div className="modal-body">
                            <p>{confirmMessage}</p>
                        </div>
                        <div className="modal-footer">
                            <button
                                className="modal-btn modal-btn-cancel"
                                onClick={handleCloseConfirmModal}
                            >
                                취소
                            </button>
                            <button
                                className="modal-btn modal-btn-primary"
                                onClick={handleConfirmModalConfirm}
                            >
                                확인
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default AccountList;